"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { ChevronDown, ChevronUp } from "lucide-react";
import { resolveLegacyLink } from "@/app/utils/linkUtils";

export function TopBar({ menuSuperieur }) {
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const items = menuSuperieur || [];
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setActiveMenu(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="hidden lg:flex justify-end items-center bg-primaryforeground px-6 py-2 text-sm">
      <nav className="flex items-center gap-6 mr-8" ref={menuRef}>
        {items.map((item) => {
          const hasSubmenu = item.submenu?.length > 0;

          // Timeout pour gestion du hover (isolé par item)
          let timeoutId: NodeJS.Timeout | null = null;

          const handleMouseEnter = () => {
            if (hasSubmenu) {
              if (timeoutId) clearTimeout(timeoutId);
              setActiveMenu(item.textItem);
            }
          };

          const handleMouseLeave = () => {
            if (hasSubmenu) {
              timeoutId = setTimeout(() => {
                setActiveMenu(null);
              }, 250);
            }
          };

          return (
            <div
              key={item.textItem} // ✅ utilise textItem comme clé unique (plus fiable)
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {hasSubmenu ? (
                <>
                  {/* Bouton avec sous-menu */}
                  <button
                    type="button"
                    onClick={() =>
                      setActiveMenu((prev) =>
                        prev === item.textItem ? null : item.textItem
                      )
                    }
                    className="flex items-center gap-1 font-semibold font-barlow text-[14px] text-foreground hover:text-primaryGreen transition"
                  >
                    <Link 
                      href={
                        item.lienItem === "Mon compte"
                          ? item.lienItem
                          : resolveLegacyLink(item.lienItem)
                      }
                    >
                      {item.textItem}
                    </Link>
                    {activeMenu === item.textItem ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </button>

                  {/* Sous-liens */}
                  {activeMenu === item.textItem && (
                    <div
                      className="absolute right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg py-2 min-w-[220px] z-50"
                      onMouseEnter={() => {
                        if (timeoutId) clearTimeout(timeoutId);
                      }}
                      onMouseLeave={() => {
                        timeoutId = setTimeout(() => {
                          setActiveMenu(null);
                        }, 250);
                      }}
                    >
                      {item.submenu.map((sub) => (
                        <Link 
                          key={sub.subText}
                          href={resolveLegacyLink(sub.subLink || "#")}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setActiveMenu(null)} // Optionnel : fermer après clic
                        >
                          {sub.subText}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                // Lien simple (sans sous-menu)
                <Link 
                  href={resolveLegacyLink(item.lienItem || "#")}
                  className="font-semibold font-barlow text-[14px] text-foreground hover:text-primaryGreen transition"
                >
                  {item.textItem}
                </Link>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );
}